import React, { createContext, useContext, useState, useEffect } from 'react';

// Smart API URL detection:
// ALWAYS use window.location.origin for API calls
// This ensures the frontend always calls its own backend regardless of build-time env vars
const getApiUrl = () => {
  const currentOrigin = window.location.origin;
  console.log('[Auth] Using runtime origin:', currentOrigin);
  return currentOrigin;
};

const API_URL = getApiUrl();

// Debug: Log the API URL being used
console.log('[Auth] Final API_URL:', API_URL);
console.log('[Auth] window.location.origin:', window.location.origin);

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Load user from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('auth_token');
    const storedUser = localStorage.getItem('auth_user');
    
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      setIsAuthenticated(true);
      // Verify token is still valid
      verifyToken(storedToken);
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async (authToken) => {
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 8000);
      const response = await fetch(`${API_URL}/api/user-auth/me?token=${authToken}`, {
        signal: controller.signal
      });
      clearTimeout(timer);
      const data = await response.json();
      if (data.success && data.user) {
        setUser(data.user);
        localStorage.setItem('auth_user', JSON.stringify(data.user));
        setIsAuthenticated(true);
      } else {
        logout();
      }
    } catch (error) {
      // On timeout/network error, keep the cached user data — don't log out
      console.warn('Token verification failed, using cached data:', error.message);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    try {
      const res = await fetch(`${API_URL}/api/user-auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
        signal: controller.signal
      });
      const data = await res.json();
      if (data.success) {
        setToken(data.token);
        setUser(data.user);
        setIsAuthenticated(true);
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('auth_user', JSON.stringify(data.user));
        return { success: true, user: data.user };
      }
      return { success: false, error: data.detail || data.message || 'Invalid email or password' };
    } catch (e) {
      if (e.name === 'AbortError') return { success: false, error: 'Request timed out — please try again' };
      return { success: false, error: 'Network error — please check your connection' };
    } finally {
      clearTimeout(timer);
    }
  };

  const register = async (userData) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    try {
      const res = await fetch(`${API_URL}/api/user-auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData),
        signal: controller.signal
      });
      const data = await res.json();
      if (data.success) {
        setToken(data.token);
        setUser(data.user);
        setIsAuthenticated(true);
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('auth_user', JSON.stringify(data.user));
        return { success: true, user: data.user };
      }
      return { success: false, error: data.detail || data.message || 'Registration failed' };
    } catch (e) {
      if (e.name === 'AbortError') return { success: false, error: 'Request timed out — please try again' };
      return { success: false, error: 'Network error — please check your connection' };
    } finally {
      clearTimeout(timer);
    }
  };

  const loginWithGoogle = async (code, redirectUri) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    try {
      const res = await fetch(`${API_URL}/api/user-auth/google/callback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, redirect_uri: redirectUri }),
        signal: controller.signal
      });
      const data = await res.json();
      if (data.success) {
        setToken(data.token);
        setUser(data.user);
        setIsAuthenticated(true);
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('auth_user', JSON.stringify(data.user));
        return { success: true, user: data.user };
      }
      return { success: false, error: data.detail || 'Google login failed' };
    } catch (e) {
      if (e.name === 'AbortError') return { success: false, error: 'Request timed out — please try again' };
      return { success: false, error: 'Network error' };
    } finally {
      clearTimeout(timer);
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    // Prevent the legacy unscoped calendar cache from leaking into the next
    // user that signs in on this browser. Per-user cached events live in
    // `local_calendar_events__{user_id}` and are kept across logout/login
    // so a returning user sees their own data instantly.
    localStorage.removeItem('local_calendar_events');
  };

  const updateProfile = async (updates) => {
    try {
      const response = await fetch(`${API_URL}/api/user-auth/profile?token=${token}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setUser(data.user);
        localStorage.setItem('auth_user', JSON.stringify(data.user));
        return { success: true, user: data.user };
      } else {
        return { success: false, error: data.detail || 'Update failed' };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const completeOnboarding = async () => {
    return updateProfile({ onboarding_completed: true });
  };

  // Refresh user data from server (useful after subscription changes)
  const refreshUserData = async () => {
    if (!token) return null;
    try {
      const response = await fetch(`${API_URL}/api/user-auth/me?token=${token}`);
      const data = await response.json();
      if (data.success && data.user) {
        setUser(data.user);
        localStorage.setItem('auth_user', JSON.stringify(data.user));
        return data.user;
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
    return null;
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      isAuthenticated,
      login,
      register,
      loginWithGoogle,
      logout,
      updateProfile,
      completeOnboarding,
      refreshUserData
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Internal: pull the raw / unscoped user_id from the auth state.
// Used by both useUserId (which may scope it per-role for the counterfind
// tenant) and useBaseUserId (which always returns the raw id — needed for
// account-level operations like profile updates).
const _resolveBaseUserId = (user, token) => {
  if (user?.user_id) return user.user_id;
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.user_id) return payload.user_id;
    } catch (e) {
      console.error('Failed to decode token for user_id:', e);
    }
  }
  return user?.email || null;
};

// Legacy hook for backward compatibility — and now the per-role scoper for
// the counterfind@operatr.com tenant. When logged in as counterfind, the
// returned user_id is suffixed with `__cf_{role}` (e.g.
// `user_daaf0c72b25315ae__cf_sales`) so that every existing API endpoint
// that filters by `user_id` automatically gives each role its own isolated
// integrations + pages.
export const useUserId = () => {
  const { user, token } = useAuth();
  const baseId = _resolveBaseUserId(user, token);
  const isCounterfind = (user?.email || '').toLowerCase() === 'counterfind@operatr.com';

  // Reactive subscription to the counterfind role stored in localStorage by
  // the sidebar. Re-renders whenever the active role changes so every
  // useUserId consumer immediately re-fetches role-scoped data.
  const COUNTERFIND_ROLE_KEY = 'sidebar_view_mode_counterfind@operatr.com';
  const [role, setRole] = useState(() => {
    if (!isCounterfind || typeof window === 'undefined') return null;
    return localStorage.getItem(COUNTERFIND_ROLE_KEY) || 'executive';
  });
  useEffect(() => {
    if (!isCounterfind) {
      // Reset if user switches account
      if (role !== null) setRole(null);
      return;
    }
    setRole(localStorage.getItem(COUNTERFIND_ROLE_KEY) || 'executive');

    const onStorage = (e) => {
      if (e.key === COUNTERFIND_ROLE_KEY) setRole(e.newValue || 'executive');
    };
    const onFocus = () => setRole(localStorage.getItem(COUNTERFIND_ROLE_KEY) || 'executive');
    // Same-tab updates from the sidebar don't fire `storage` events; poll
    // once a second to pick them up. Cheap, only runs for counterfind.
    const interval = setInterval(() => {
      const v = localStorage.getItem(COUNTERFIND_ROLE_KEY) || 'executive';
      setRole(prev => (prev === v ? prev : v));
    }, 1000);
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
      clearInterval(interval);
    };
    // We intentionally do NOT include `role` in the deps — that would re-create
    // the interval on every change. The effect re-runs only when isCounterfind
    // flips (i.e. on login/logout).
    // eslint-disable-line
  }, [isCounterfind]);

  if (isCounterfind && baseId && role) {
    return `${baseId}__cf_${role}`;
  }
  return baseId;
};

// Returns the raw, unscoped user_id regardless of any per-role scoping.
// Use this for account-level operations (profile updates, subscription
// info, billing) where the data belongs to the underlying auth account,
// not a specific role.
export const useBaseUserId = () => {
  const { user, token } = useAuth();
  return _resolveBaseUserId(user, token);
};

// Hook to get user email
export const useUserEmail = () => {
  const { user, token } = useAuth();
  
  // Try to get email from the user object first
  if (user?.email) {
    return user.email;
  }
  
  // Fallback: try to decode from token
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.email) {
        return payload.email;
      }
    } catch (e) {
      console.error('Failed to decode token for email:', e);
    }
  }
  
  return null;
};

export default AuthContext;
